import { Category } from "../components/CategoryMegaMenu";

/**
 * Builds a category path string from a category hierarchy array
 * Uses slug if available, otherwise falls back to ID
 * @param categoryPath - Array of categories representing the hierarchy path
 * @returns Path string like "electronics/consumer-electronics/bluetooth-headphones"
 */
export function buildCategoryPath(categoryPath: Category[]): string {
  if (!categoryPath || categoryPath.length === 0) {
    return "";
  }

  return categoryPath
    .map((category) => {
      // Use slug if available, otherwise use ID
      return category.slug || category.id;
    })
    .join("/");
}

/**
 * Finds a category by ID in the category tree
 */
function findCategoryById(
  categories: Category[],
  id: string
): Category | null {
  for (const category of categories) {
    if (category.id === id) {
      return category;
    }
    if (category.children) {
      const found = findCategoryById(category.children, id);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Finds a category by slug in the category tree
 */
function findCategoryBySlug(
  categories: Category[],
  slug: string
): Category | null {
  for (const category of categories) {
    if (category.slug === slug) {
      return category;
    }
    if (category.children) {
      const found = findCategoryBySlug(category.children, slug);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Parses a category path string back to a category array
 * Validates that the path exists in the category tree
 * @param path - Path string like "electronics/consumer-electronics/bluetooth-headphones"
 * @param categories - Full category tree to search in
 * @returns Array of categories representing the path, or null if path is invalid
 */
export function parseCategoryPath(
  path: string,
  categories: Category[]
): Category[] | null {
  if (!path || path.trim() === "") {
    return null;
  }

  const pathSegments = path.split("/").filter((segment) => segment.trim() !== "");
  if (pathSegments.length === 0) {
    return null;
  }

  const result: Category[] = [];
  let currentCategories = categories;

  for (const segment of pathSegments) {
    // Try to find by slug first, then by ID
    let found: Category | null = findCategoryBySlug(currentCategories, segment);
    if (!found) {
      found = findCategoryById(currentCategories, segment);
    }

    if (!found) {
      // Path segment not found, invalid path
      return null;
    }

    result.push(found);

    // Move to children for next iteration
    if (found.children && found.children.length > 0) {
      currentCategories = found.children;
    } else {
      // No more children, but we might have more segments
      // This could be valid if the path is longer than the actual hierarchy
      // We'll continue but won't be able to validate further
      break;
    }
  }

  return result.length > 0 ? result : null;
}

/**
 * Gets the full category path string from a category ID
 * Traverses up the parent chain to build the complete path
 * @param categoryId - The category ID to get the path for
 * @param categories - Full category tree to search in
 * @returns Path string like "electronics/consumer-electronics/bluetooth-headphones"
 */
export function getCategoryPathFromId(
  categoryId: string,
  categories: Category[]
): string {
  if (!categoryId || !categories || categories.length === 0) {
    return "";
  }

  // Find the category and build path by traversing up
  const findPath = (
    cats: Category[],
    id: string,
    path: Category[] = []
  ): Category[] | null => {
    for (const category of cats) {
      const currentPath = [...path, category];
      if (category.id === id) {
        return currentPath;
      }
      if (category.children) {
        const found = findPath(category.children, id, currentPath);
        if (found) return found;
      }
    }
    return null;
  };

  const categoryPath = findPath(categories, categoryId);
  if (!categoryPath) {
    return "";
  }

  return buildCategoryPath(categoryPath);
}

/**
 * Gets the category path array from a category ID
 * @param categoryId - The category ID to get the path for
 * @param categories - Full category tree to search in
 * @returns Array of categories representing the path
 */
export function getCategoryPathArrayFromId(
  categoryId: string,
  categories: Category[]
): Category[] | null {
  if (!categoryId || !categories || categories.length === 0) {
    return null;
  }

  const findPath = (
    cats: Category[],
    id: string,
    path: Category[] = []
  ): Category[] | null => {
    for (const category of cats) {
      const currentPath = [...path, category];
      if (category.id === id) {
        return currentPath;
      }
      if (category.children) {
        const found = findPath(category.children, id, currentPath);
        if (found) return found;
      }
    }
    return null;
  };

  return findPath(categories, categoryId);
}

